import { Box } from "@mui/material";
import { useTripInputWaypoints } from "../../../features/riskViewer/riskViewerSlice";
import { useEffect, useRef, useState } from "react";
import { mapBoxAccessToken } from "../../../app/env";
import { WAYPOINT_DISTANCE_WARNING } from "../../UserVisibleStrings";


const getRouteDuration = async ( lat1: number, lon1: number, lat2: number, lon2: number, cache: Map<string, number> ): Promise<number> =>
{
    const key = `${lat1},${lon1}|${lat2},${lon2}`;

    // Check cache first
    if ( cache.has( key ) )
    {
        const cached = cache.get( key );
        if ( cached !== undefined )
        {
            return cached;
        }
    }

    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${lon1},${lat1};${lon2},${lat2}?access_token=${mapBoxAccessToken}`;
    const response = await fetch( url );
    if ( !response.ok )
    {
        throw new Error( "Failed to fetch route data from Mapbox" );
    }

    const data = await response.json();
    const duration = data.routes?.[0]?.duration ?? 0; // in hours

    // Cache result
    cache.set( key, duration );

    return duration;
};

type AdvisoryDistanceErrorProps = {
    isAdvisory: boolean,
};

const AdvisoryDistanceError = ( { isAdvisory }: AdvisoryDistanceErrorProps ) =>
{
    const tripInputWaypoints = useTripInputWaypoints();
    const [ shouldShowError, setShouldShowError ] = useState<boolean>( false );
    const routeDurationCacheRef = useRef<Map<string, number>>( new Map() );

    useEffect( () =>
    {
        const checkDistances = async () =>
        {
            if ( !tripInputWaypoints || tripInputWaypoints.length < 2 )
            {
                setShouldShowError( false );
                return;
            }

            for ( let i = 0; i < tripInputWaypoints.length - 1; i++ )
            {
                const wp1 = tripInputWaypoints[i]?.location?.location;
                const wp2 = tripInputWaypoints[i + 1]?.location?.location;

                // Validate both waypoints are defined and have numeric lat/lon
                const isValid = ( wp: { latitude: number, longitude: number } ): boolean => wp !== undefined &&
                    wp.latitude !== 0 &&
                    wp.longitude !== 0;

                if ( !isValid( wp1 ) || !isValid( wp2 ) )
                {
                    continue; // Skip to next pair
                }

                try
                {
                    const duration = await getRouteDuration(
                        wp1.latitude, wp1.longitude, wp2.latitude, wp2.longitude, routeDurationCacheRef.current,
                    );
                    if ( duration > 39600 ) // 11 hours
                    {
                        setShouldShowError( true );
                        return;
                    }
                }
                catch ( error )
                {
                    console.error( "Error checking route duration:", error );
                }
            }

            setShouldShowError( false );
        };

        checkDistances().catch( ( err ) =>
        {
            console.error( "Error in checkDistances:", err );
        } );

        return () =>
        {
            routeDurationCacheRef.current.clear();
        };
    }, [ tripInputWaypoints ] );

    if ( shouldShowError && isAdvisory )
    {
        return (
            <Box sx = {{ color: "warning.main", fontWeight: 500 }}>
                {WAYPOINT_DISTANCE_WARNING}
            </Box>
        );
    }
    return null;
};

export default AdvisoryDistanceError;
